import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Sparkles, MessageSquare, Minus, Maximize2, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from './Toast';
import API_URL from '../config/API_URL';

const FloatingAIAssistant = ({ contacts = [] }) => {
  const [isOpen, setIsOpen] = useState(true); // Changed to true - now opens by default
  const [isMinimized, setIsMinimized] = useState(true); // Changed to true - starts minimized
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedContact, setSelectedContact] = useState('all');
  
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const { showSuccess, showError } = useToast();

  // Calculate position dynamically based on window size
  const getPosition = () => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const containerWidth = 420;
    const containerHeight = 600;
    
    return {
      x: Math.max(20, windowWidth - containerWidth - 40), // 40px from right edge
      y: Math.min(100, windowHeight - containerHeight - 40) // Keep within viewport
    };
  };

  const [position, setPosition] = useState(getPosition());

  // Update position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (!isDragging) {
        setPosition(getPosition());
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDragging]);

  const styles = {
    toggleButton: {
      position: 'fixed',
      right: '2rem',
      bottom: '2rem',
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      zIndex: 9999,
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    container: {
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
      width: '420px',
      maxWidth: 'calc(100vw - 4rem)',
      height: isMinimized ? 'auto' : '600px',
      maxHeight: 'calc(100vh - 4rem)',
      background: '#ffffff',
      borderRadius: '20px',
      boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(102, 126, 234, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 9999,
      overflow: 'hidden',
      backdropFilter: 'blur(20px)',
      border: '2px solid rgba(102, 126, 234, 0.2)',
    },
    header: {
      padding: '1.25rem',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: isDragging ? 'grabbing' : 'grab',
      userSelect: 'none',
      position: 'relative',
      overflow: 'hidden',
    },
    headerBackground: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgba(255,255,255,0.2) 0%, transparent 50%)`,
      pointerEvents: 'none',
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      flex: 1,
      zIndex: 1,
      pointerEvents: 'none',
    },
    headerTitle: {
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      fontSize: '1.15rem',
      letterSpacing: '-0.02em',
    },
    headerSubtitle: {
      fontSize: '0.75rem',
      opacity: 0.9,
      marginTop: '0.1rem',
      fontWeight: 400,
    },
    headerActions: {
      display: 'flex',
      gap: '0.5rem',
      zIndex: 10,
    },
    headerButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '8px',
      padding: '0.5rem',
      cursor: 'pointer',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 200ms',
      backdropFilter: 'blur(10px)',
    },
    contextSelector: {
      padding: '1rem',
      borderBottom: '1px solid #e2e8f0',
      background: 'linear-gradient(to bottom, #f8fafc 0%, #ffffff 100%)',
    },
    contextLabel: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: '#475569',
      marginBottom: '0.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    contextSelect: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '10px',
      fontSize: '0.9rem',
      fontFamily: 'inherit',
      background: '#ffffff',
      cursor: 'pointer',
      transition: 'all 200ms',
      outline: 'none',
      fontWeight: 500,
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      background: 'linear-gradient(to bottom, #fafbfc 0%, #f8fafc 100%)',
    },
    message: {
      padding: '1rem',
      borderRadius: '16px',
      fontSize: '0.9rem',
      lineHeight: 1.6,
      position: 'relative',
      maxWidth: '90%',
      wordWrap: 'break-word',
    },
    userMessage: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      alignSelf: 'flex-end',
      marginLeft: 'auto',
      borderBottomRightRadius: '4px',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    },
    aiMessage: {
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      color: '#0f172a',
      alignSelf: 'flex-start',
      borderBottomLeftRadius: '4px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    messageLabel: {
      fontSize: '0.7rem',
      fontWeight: 700,
      marginBottom: '0.5rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      opacity: 0.7,
    },
    messageText: {
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
    },
    inputContainer: {
      padding: '1rem',
      borderTop: '2px solid #e2e8f0',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    quickPrompts: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    quickPrompt: {
      padding: '0.5rem 0.875rem',
      background: '#f1f5f9',
      border: '1px solid #e2e8f0',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 200ms',
      color: '#475569',
      whiteSpace: 'nowrap',
    },
    inputWrapper: {
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'flex-end',
    },
    textarea: {
      flex: 1,
      padding: '0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.9rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      resize: 'none',
      minHeight: '44px',
      maxHeight: '120px',
      transition: 'all 200ms',
    },
    sendButton: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      borderRadius: '10px',
      padding: '0.875rem 1rem',
      cursor: 'pointer',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 200ms',
      minWidth: '48px',
      height: '48px',
      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      color: '#64748b',
      textAlign: 'center',
      height: '100%',
      gap: '1rem',
    },
    emptyIcon: {
      opacity: 0.5,
    },
    emptyTitle: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: '#475569',
      fontFamily: "'Syne', sans-serif",
    },
    emptyText: {
      fontSize: '0.9rem',
      lineHeight: 1.6,
      color: '#64748b',
    },
    loadingDots: {
      display: 'flex',
      gap: '0.35rem',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
    },
    loadingDot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: '#667eea',
    },
    minimizedPreview: {
      padding: '1rem 1.25rem',
      fontSize: '0.85rem',
      color: '#64748b',
      textAlign: 'center',
      fontStyle: 'italic',
    },
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;
        
        // Keep within viewport bounds
        const maxX = window.innerWidth - 420;
        const maxY = window.innerHeight - 600;
        
        setPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY))
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleHeaderMouseDown = (e) => {
    // Only start dragging if clicking on header (not buttons)
    if (e.target.closest('button')) return;
    
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
    setIsDragging(true);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const quickPrompts = [
    "What was my last interaction?",
    "Who should I reach out to?",
    "Show recent activity",
    "Best contact for finance advice?"
  ];

  const handleQuickPrompt = (prompt) => {
    setInputValue(prompt);
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setIsGenerating(true);

    const updatedMessages = [
      ...messages,
      { role: 'user', content: userMessage }
    ];
    setMessages(updatedMessages);

    try {
      const response = await fetch(`${API_URL}/api/ai-assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          contact_id: selectedContact !== 'all' ? parseInt(selectedContact) : null,
          conversation_history: updatedMessages
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessages([
          ...updatedMessages,
          { role: 'assistant', content: data.response }
        ]);
      } else {
        showError(data.error || 'Failed to get response');
        setMessages([
          ...updatedMessages,
          { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }
        ]);
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      showError('Failed to connect to AI assistant');
      setMessages([
        ...updatedMessages,
        { role: 'assistant', content: 'Sorry, I could not connect to the server. Please check your connection and try again.' }
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const LoadingDots = () => (
    <div style={styles.loadingDots}>
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
      />
    </div>
  );

  // Show sparkle button when component is closed
  if (!isOpen) {
    return (
      <motion.button
        onClick={() => {
          setIsOpen(true);
          setIsMinimized(true); // Open in minimized state
        }}
        style={styles.toggleButton}
        whileHover={{ scale: 1.1, boxShadow: '0 12px 32px rgba(102, 126, 234, 0.5)' }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 15 }}
      >
        <Sparkles size={28} />
      </motion.button>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      style={styles.container}
    >
      {/* Header */}
      <div 
        style={styles.header}
        onMouseDown={handleHeaderMouseDown}
      >
        <div style={styles.headerBackground} />
        <div style={styles.headerContent}>
          <MessageSquare size={24} />
          <div>
            <div style={styles.headerTitle}>AI Assistant</div>
            <div style={styles.headerSubtitle}>Your networking copilot</div>
          </div>
        </div>
        <div style={styles.headerActions}>
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(!isMinimized);
            }}
            style={styles.headerButton}
            whileHover={{ background: 'rgba(255, 255, 255, 0.3)' }}
            whileTap={{ scale: 0.95 }}
          >
            {isMinimized ? <Maximize2 size={16} /> : <Minus size={16} />}
          </motion.button>
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
              setIsMinimized(true); // Reset to minimized state when reopening
            }}
            style={styles.headerButton}
            whileHover={{ background: 'rgba(255, 255, 255, 0.3)' }}
            whileTap={{ scale: 0.95 }}
          >
            <X size={18} />
          </motion.button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Context Selector */}
          <div style={styles.contextSelector}>
            <label style={styles.contextLabel}>
              <User size={14} />
              Focus Context
            </label>
            <select
              value={selectedContact}
              onChange={(e) => setSelectedContact(e.target.value)}
              style={styles.contextSelect}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="all">All Contacts & General</option>
              {contacts?.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name} {contact.company ? `- ${contact.company}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Messages Container */}
          <div style={styles.messagesContainer}>
            {messages.length === 0 ? (
              <div style={styles.emptyState}>
                <Sparkles size={48} style={styles.emptyIcon} />
                <div style={styles.emptyTitle}>Ask Me Anything</div>
                <div style={styles.emptyText}>
                  I can help you find contacts, recall interactions, suggest who to reach out to, and more.
                </div>
                <div style={styles.quickPrompts}>
                  {quickPrompts.map((prompt, idx) => (
                    <div
                      key={idx}
                      style={styles.quickPrompt}
                      onClick={() => handleQuickPrompt(prompt)}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#e2e8f0';
                        e.target.style.borderColor = '#cbd5e1';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#f1f5f9';
                        e.target.style.borderColor = '#e2e8f0';
                      }}
                    >
                      {prompt}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <AnimatePresence>
                  {messages.map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        ...styles.message,
                        ...(msg.role === 'user' ? styles.userMessage : styles.aiMessage)
                      }}
                    >
                      <div style={styles.messageLabel}>
                        {msg.role === 'user' ? 'You' : 'AI Assistant'}
                      </div>
                      <div style={styles.messageText}>{msg.content}</div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {isGenerating && <LoadingDots />}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Container */}
          <div style={styles.inputContainer}>
            <div style={styles.inputWrapper}>
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about contacts, interactions, or networking advice..."
                style={styles.textarea}
                rows={1}
                onInput={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.background = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <motion.button
                onClick={handleSend}
                disabled={isGenerating || !inputValue.trim()}
                style={{
                  ...styles.sendButton,
                  opacity: isGenerating || !inputValue.trim() ? 0.5 : 1,
                  cursor: isGenerating || !inputValue.trim() ? 'not-allowed' : 'pointer',
                }}
                whileHover={!isGenerating && inputValue.trim() ? { 
                  scale: 1.05,
                  boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)'
                } : {}}
                whileTap={!isGenerating && inputValue.trim() ? { scale: 0.95 } : {}}
              >
                {isGenerating ? (
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  >
                    <Sparkles size={20} />
                  </motion.div>
                ) : (
                  <Send size={20} />
                )}
              </motion.button>
            </div>
          </div>
        </>
      )}

      {isMinimized && (
        <div style={styles.minimizedPreview}>
          Click to expand your AI assistant
        </div>
      )}
    </motion.div>
  );
};

export default FloatingAIAssistant;